import { Fragment, useCallback, useMemo } from 'react';
import { ResourcesHubIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import {
  useSeenTransportIds,
  formatIngestionSource,
  ingestionSourceIsUnite,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  trackNewResourceRequestClick,
  trackResourceRequestQueueItemClick,
  trackExternalResourceRequestOrderClick,
} from '../../../../../gainsightTrackings/resourceRequestTrackings';
import {
  useResourceRequestRegistryLists,
  type ResourceRequestAdapter,
  type ResourceRequestDetailView,
  type ResourceRequestPickupLocation,
  type ResourceRequestQueueCategory,
} from '../adapters';
import { useResourceRequestClock } from '../useResourceRequestClock';
import { PanelCloseButton } from './PanelCloseButton';
import QueueSection from './QueueSection';
import {
  ListHeaderRow,
  EmptyStateBox,
  HeaderActions,
  ListPanelContent,
  EmptyStatePadding,
  PanelHeaderRow,
  PanelHeaderTitle,
  NewRequestButton,
} from './RequestListView.styles';

/**
 * Queue sections in render order
 */
const QUEUE_SECTIONS: {
  category: ResourceRequestQueueCategory;
  title: string;
  defaultExpanded: boolean;
}[] = [
  { category: 'active', title: 'Active', defaultExpanded: true },
  { category: 'error', title: 'Error', defaultExpanded: true },
  { category: 'closed', title: 'Closed', defaultExpanded: false },
];

function trackQueueItemClick<T>(
  adapter: ResourceRequestAdapter<T>,
  request: T,
  requestId: string,
): void {
  const ingestionMeta = adapter.getIngestionMetadata?.(request);
  const ingestionSource = ingestionMeta?.ingestionSource ?? null;
  const requestMethod = ingestionMeta?.requestMethod ?? null;
  const sourceLabel = formatIngestionSource(ingestionSource, requestMethod);

  trackResourceRequestQueueItemClick({
    resourceType: adapter.id,
    requestId,
    sourceLabel,
  });

  if (ingestionSource != null && !ingestionSourceIsUnite(ingestionSource)) {
    trackExternalResourceRequestOrderClick({
      resourceType: adapter.id,
      requestId,
      ingestionSource,
      requestMethod,
      sourceLabel,
    });
  }
}

function requestCreatedAtIso(request: unknown): string | null {
  if (
    typeof request === 'object' &&
    request !== null &&
    'created_at' in request
  ) {
    const value = (request as { created_at: unknown }).created_at;
    return typeof value === 'string' ? value : null;
  }
  return null;
}

interface RequestListViewProps {
  onNewRequest: () => void;
  onSelectRequest: (
    detailView: ResourceRequestDetailView,
    requestId: string,
    pickupLocation: ResourceRequestPickupLocation | null,
  ) => void;
}

function RequestListView({
  onNewRequest,
  onSelectRequest,
}: RequestListViewProps) {
  const seenTransportIds = useSeenTransportIds();
  const listSlices = useResourceRequestRegistryLists();
  const nowMs = useResourceRequestClock();

  const rowsByCategory = useMemo(() => {
    const flat = listSlices.flatMap(({ adapter, requests }) =>
      requests.map(request => ({ adapter, request })),
    );
    const sorted = [...flat].sort((a, b) => {
      const aTime = requestCreatedAtIso(a.request) ?? '';
      const bTime = requestCreatedAtIso(b.request) ?? '';
      return bTime.localeCompare(aTime);
    });

    const groups: Record<ResourceRequestQueueCategory, typeof sorted> = {
      active: [],
      error: [],
      closed: [],
    };
    for (const row of sorted) {
      const category =
        row.adapter.getQueueCategory?.(row.request, nowMs) ?? 'active';
      groups[category].push(row);
    }
    return groups;
  }, [listSlices, nowMs]);

  // Count only the categories that have a rendered section, so the same source
  // drives both the empty state and what's shown. A category with no section
  // (e.g. an as-yet-unwired one) must not suppress the empty state while being
  // invisible.
  const totalCount = QUEUE_SECTIONS.reduce(
    (sum, { category }) => sum + rowsByCategory[category].length,
    0,
  );

  const handleSelectRequest = useCallback(
    (
      detailView: ResourceRequestDetailView,
      requestId: string,
      pickupLocation: ResourceRequestPickupLocation | null,
    ) => {
      onSelectRequest(detailView, requestId, pickupLocation);
    },
    [onSelectRequest],
  );

  return (
    <>
      <PanelHeaderRow>
        <PanelHeaderTitle>
          <ResourcesHubIcon width={16} height={16} color="primaryText" />
          <Text
            color="primaryText"
            size="large"
            fontWeight="bold"
            data-name="resource-panel-title"
          >
            Resources & Tools
          </Text>
        </PanelHeaderTitle>
        <PanelCloseButton />
      </PanelHeaderRow>
      <ListHeaderRow>
        <Text
          color="secondaryText"
          size="normal"
          data-name="resource-panel-title"
        >
          Resource Requests
        </Text>
        <HeaderActions>
          <NewRequestButton
            btnType="primary"
            size="small"
            noMargin
            onClick={() => {
              trackNewResourceRequestClick();
              onNewRequest();
            }}
            dataName="resource-panel-new-request-button"
          >
            + New Request
          </NewRequestButton>
        </HeaderActions>
      </ListHeaderRow>
      <ListPanelContent>
        {totalCount === 0 ? (
          <EmptyStatePadding>
            <EmptyStateBox>
              <Text color="secondaryText" size="normal">
                No Resource Requests available
              </Text>
            </EmptyStateBox>
          </EmptyStatePadding>
        ) : (
          QUEUE_SECTIONS.map(({ category, title, defaultExpanded }) => {
            const rows = rowsByCategory[category];
            if (rows.length === 0) {
              return null;
            }
            const hasUnread = rows.some(
              ({ adapter, request }) =>
                !seenTransportIds.includes(adapter.getId(request)),
            );
            return (
              <QueueSection
                key={category}
                title={title}
                count={rows.length}
                hasUnread={hasUnread}
                defaultExpanded={defaultExpanded}
              >
                {rows.map(({ adapter, request }) => {
                  const id = adapter.getId(request);
                  const { ListItemComponent } = adapter;
                  return (
                    <Fragment key={`${adapter.id}-${id}`}>
                      <ListItemComponent
                        request={request}
                        isUnread={!seenTransportIds.includes(id)}
                        onClick={() => {
                          trackQueueItemClick(adapter, request, id);
                          handleSelectRequest(
                            adapter.id,
                            id,
                            adapter.getPickupLocation?.(request) ?? null,
                          );
                        }}
                      />
                    </Fragment>
                  );
                })}
              </QueueSection>
            );
          })
        )}
      </ListPanelContent>
    </>
  );
}

export default RequestListView;
